<script setup lang="ts">
	import { useHotelsStore } from '@/stores/hotels'
	import { storeToRefs } from 'pinia'
	import { computed, onMounted, ref } from 'vue'

	import {
		DropdownMenu,
		DropdownMenuContent,
		DropdownMenuLabel,
		DropdownMenuRadioGroup,
		DropdownMenuRadioItem,
		DropdownMenuSeparator,
		DropdownMenuTrigger,
	} from '@/components/ui/dropdown-menu'

	import { useAuth } from '@/composables/useAuth'

	import { OrderConditions } from '@/enums/orderConditions'

	import useHotelSearch from '../composables/useHotelSearch'
	import { RAIL_COLLISION_PADDING } from '../helpers/hotelFilters'
	import HotelFilterChips from './HotelFilterChips.vue'
	import HotelFiltersForm from './HotelFiltersForm.vue'

	interface Props {
		isFetching: boolean
		displayMap: boolean
		expandHotelForm: boolean
		orderBy: OrderConditions
		favoritePriceOrder: 'asc' | 'desc'
	}

	interface Emits {
		(e: 'showMap'): void
		(e: 'setNewRequest'): void
		(e: 'setOrderBy', value: OrderConditions): void
		(e: 'setFavoritePriceOrder', value: 'asc' | 'desc'): void
	}

	const props = defineProps<Props>()
	const emits = defineEmits<Emits>()

	onMounted(() => {
		if (rating.value != 0) {
			fixRating.value = true
		}
	})

	const { form, hotels, filteredHotelList, activeFilterCount } =
		storeToRefs(useHotelsStore())
	const { setSearchQuery } = useHotelsStore()

	const { user } = useAuth()

	const rating = ref(form.value.Rating ?? 0)
	const fixRating = ref(false)

	const localSearchQuery = ref('')

	const { searchQuery } = useHotelSearch(localSearchQuery)

	const showFilters = ref(false)

	const sortOptions = computed(() => [
		{ value: OrderConditions.AscendingSort, label: 'Menor precio' },
		{ value: OrderConditions.DescendingSort, label: 'Mayor precio' },
		...(!user.value?.internal && hotels.value.some((hotel) => hotel.favorite) ?
			[{ value: OrderConditions.Favorites, label: 'Favoritos' }]
		:	[]),
	])

	const sortLabel = computed(
		() =>
			sortOptions.value.find((option) => option.value === props.orderBy)
				?.label ?? 'Ordenar'
	)
</script>

<template>
	<!-- Search Input Row -->
	<div class="relative w-full">
		<div
			class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
		>
			<span class="material-symbols-outlined text-gray-600"> search </span>
		</div>

		<div class="flex items-center gap-2">
			<input
				v-model="localSearchQuery"
				:class="[
					'block w-full rounded-lg border-0 py-2.5 pl-10 pr-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#021F58]/30',
					localSearchQuery != '' &&
						'ring-2 ring-[#021F58]/30 focus:!outline-none',
				]"
				placeholder="Filtrar hoteles por nombre"
			/>

			<button
				v-if="localSearchQuery != ''"
				class="flex items-center justify-center rounded-lg bg-gray-100 p-2 hover:bg-gray-200 transition-colors"
				@click.prevent="
					() => {
						localSearchQuery = ''
						setSearchQuery('')
					}
				"
			>
				<span class="material-symbols-outlined text-gray-700"> close </span>
			</button>
		</div>
	</div>

	<!--
		Un solo renglón que envuelve por el espacio real, no por breakpoint: con el mapa
		abierto la columna es angosta aunque la pantalla sea ancha.
	-->
	<div class="flex flex-wrap items-center gap-2">
		<!-- Left side: Count + Info + Buttons -->
		<div class="flex shrink-0 items-center gap-2">
			<!-- Results Count Badge -->
			<div
				class="inline-flex items-center gap-2 bg-[#021F58]/10 text-[#021F58] px-3 py-2 rounded-lg font-bold"
			>
				<span class="material-symbols-outlined text-lg">apartment</span>
				<span class="text-base">{{ filteredHotelList.length }}</span>
				<span class="text-sm font-normal">{{
					filteredHotelList.length === 1 ? 'hotel' : 'hoteles'
				}}</span>
			</div>

			<!-- Action Buttons -->
			<div class="flex shrink-0 items-center gap-2">
				<!-- Filters Toggle Button -->
				<button
					type="button"
					@click="showFilters = !showFilters"
					:class="[
						'flex items-center gap-2 px-3 py-2 rounded-lg font-semibold shadow-md transition-all duration-200 hover:scale-105 text-sm',
						showFilters || activeFilterCount > 0 ?
							'bg-[#021F58] text-white hover:bg-[#1C3F7A]'
						:	'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
					]"
				>
					<span class="material-symbols-outlined text-lg">tune</span>
					<span class="hidden sm:inline">Filtros</span>
					<span
						v-if="activeFilterCount > 0"
						class="inline-flex items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-bold"
						:class="
							!showFilters && activeFilterCount > 0 && 'bg-white text-[#021F58]'
						"
					>
						{{ activeFilterCount }}
					</span>
					<span
						class="material-symbols-outlined text-lg transition-transform duration-200"
						:class="showFilters && 'rotate-180'"
						>expand_more</span
					>
				</button>

				<!-- Map Toggle Button -->
				<button
					v-if="!displayMap"
					type="button"
					aria-label="Ver mapa"
					@click="emits('showMap')"
					class="flex items-center gap-2 bg-[#021F58] hover:bg-[#1C3F7A] text-white px-4 py-2 rounded-lg font-semibold shadow-md transition-all duration-200 hover:scale-105 text-sm"
				>
					<span class="material-symbols-outlined text-lg">map</span>
					<span class="hidden sm:inline">Ver Mapa</span>
				</button>
			</div>
		</div>

		<!--
			Orden: botón compacto con menú, para que quepa en el mismo renglón que el
			conteo y Filtros aun en la columna angosta del mapa. En pantalla angosta no se
			pega al borde derecho: ahí vive el riel flotante (WhatsApp, comentarios).
		-->
		<DropdownMenu>
			<DropdownMenuTrigger as-child>
				<button
					type="button"
					class="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm font-medium text-gray-700 ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 sm:ml-auto"
				>
					<span
						class="material-symbols-outlined text-[18px] leading-none text-gray-400"
						aria-hidden="true"
						>swap_vert</span
					>
					{{ sortLabel }}
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				:collision-padding="RAIL_COLLISION_PADDING"
				class="w-52"
			>
				<DropdownMenuLabel class="text-xs font-medium text-gray-500"
					>Ordenar por</DropdownMenuLabel
				>
				<DropdownMenuRadioGroup
					:model-value="orderBy"
					@update:model-value="
						(value) => emits('setOrderBy', value as OrderConditions)
					"
				>
					<DropdownMenuRadioItem
						v-for="option in sortOptions"
						:key="option.value"
						:value="option.value"
					>
						{{ option.label }}
					</DropdownMenuRadioItem>
				</DropdownMenuRadioGroup>

				<!-- Dentro de Favoritos, el precio se ordena aparte. -->
				<template v-if="orderBy === OrderConditions.Favorites">
					<DropdownMenuSeparator />
					<DropdownMenuLabel class="text-xs font-medium text-gray-500"
						>Precio en favoritos</DropdownMenuLabel
					>
					<DropdownMenuRadioGroup
						:model-value="favoritePriceOrder"
						@update:model-value="
							(value) => emits('setFavoritePriceOrder', value as 'asc' | 'desc')
						"
					>
						<DropdownMenuRadioItem value="asc"
							>Menor precio</DropdownMenuRadioItem
						>
						<DropdownMenuRadioItem value="desc"
							>Mayor precio</DropdownMenuRadioItem
						>
					</DropdownMenuRadioGroup>
				</template>
			</DropdownMenuContent>
		</DropdownMenu>
	</div>

	<!-- Lo aplicado y los filtros rápidos: visibles aunque el panel esté cerrado. -->
	<HotelFilterChips />

	<!-- Filtros: panel colapsable detrás del botón "Filtros" -->
	<Transition
		enter-active-class="transition-all duration-200 ease-out"
		enter-from-class="opacity-0 -translate-y-1"
		enter-to-class="opacity-100 translate-y-0"
		leave-active-class="transition-all duration-150 ease-in"
		leave-from-class="opacity-100 translate-y-0"
		leave-to-class="opacity-0 -translate-y-1"
	>
		<HotelFiltersForm v-if="showFilters" />
	</Transition>
</template>

<style>
	:root {
		--popper-theme-background-color: #333333;
		--popper-theme-background-color-hover: #333333;
		--popper-theme-text-color: #ffffff;
		--popper-theme-border-width: 0px;
		--popper-theme-border-style: solid;
		--popper-theme-border-radius: 6px;
		--popper-theme-padding: 12px;
		--popper-theme-box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
	}

	.filter-button {
		@apply rounded-md border-2 border-transparent bg-blue-500 px-2 py-1 text-xs focus:border-none hover:border-white;
	}
	.filter-applied {
		@apply flex items-center gap-2 rounded-md border px-2 py-1 text-sm transition-colors duration-300;
	}
	.remove-filter {
		@apply rounded-xl bg-gray-200 p-[1px] px-1 text-xs transition-colors duration-300 hover:bg-gray-300;
	}
	.reset-button {
		@apply ml-2 rounded-md border-2 border-transparent bg-red-500 p-0.5 text-sm font-semibold text-white hover:border-red-700 hover:bg-red-600;
	}
</style>
